import { Option } from '@shared/components/select-new/Select';
import { IComment } from '@shared/models/comment';
import { FieldConfigs } from '@shared/models/config';
import { BasicUser } from '@shared/models/user';
import { InterestScheduleStatusDropdownOption } from '@shared/utils/interest-schedule';

// ------ Core -------------------------------------------------------------------------------------

export enum DiscountedReason {
  Location = 1,
  Budget = 2,
  SizeTooSmall = 3,
  SizeTooBig = 4,
  Space = 5,
  Specification = 6,
  Terms = 7,
  Staying = 8,
  RequirementsChanged = 9,
  SignedElsewhere = 10,
  OnHold = 11,
  Cancelled = 12,
  NoResponse = 14,
  Unknown = 15,
  // TODO - Requirement closed for some reason
  TakenOffMarket = 16, // TODO - Disposal closed for some reason
  ReasonChoseJointListing = 17,
  ReasonJunk = 18,
  ReasonTimingNotRight = 19,
  ReasonMarketResearchCompSchedule = 20,
  ReasonShortlistedElsewhere = 21,
}

// ------ Availability Schedule --------------------------------------------------------------------

export enum AvailabilityScheduleStatuses {
  Inbound = 0,
  Longlist = 1,
  Shortlist = 2,
  Viewing = 3,
  Negotiating = 4,
  Removed = 5,
  ViewingCompleted = 6,
  OfferSubmitted = 7,
  TermsIssued = 8,
  UnderOffer = 9,
  Exchanged = 10,
  Signed = 11,
  Confidential = 1337,
}

export interface AvailabilityScheduleShowFeatureFlags {
  agent_messages_visible: boolean;
  can_set_financials_team_intel: boolean;
  match_pending: boolean;
  taken_off_marketplace: boolean;
}

// ------ Interest Schedule ------------------------------------------------------------------------

export type InterestCompanyFieldKeys = 'agent_companies' | 'tenant_companies';
export type InterestContactFieldKeys = 'agent_contacts' | 'tenant_contacts';

export enum InterestScheduleStatuses {
  Outbound = 0,
  Prospect = 1,
  Enquiries = 2,
  Viewing = 3,
  Negotiating = 4,
  Removed = 5,
  ViewingCompleted = 6,
  OfferReceived = 7,
  TermsIssued = 8,
  UnderOffer = 9,
  Exchanged = 10,
  Signed = 11,
  Inbound = 12,
  Idle = 13,
}

export interface InterestScheduleIndexFeatureFlags {
  can_manage_requirement: boolean;
  can_record_letting_or_sale: boolean;
  can_set_tenant_intel: boolean;
  manage_interest_schedule_presentation: boolean;
  manage_interest_schedule_shared: boolean;
  manage_viewings: boolean;
  taken_off_marketplace: boolean;
}

export interface InterestScheduleShowFeatureFlags {
  agent_messages_visible: boolean;
  can_access_marketplace_requirement: boolean;
  can_copy_linked_requirement: boolean;
  can_stop_syncing_with_requirement: boolean;
  can_start_syncing_with_requirement: boolean;
  can_link_interest_to_requirement: boolean;
  can_manage_requirement: boolean;
  can_manage_tenant_confidentiality: boolean;
  can_record_letting_or_sale: boolean;
  can_set_tenant_name: boolean;
  create_viewing: boolean;
  manage_interest_schedule_fields: boolean;
  manage_interest_schedule_presentation: boolean;
  manage_interest_schedule_shared: boolean;
  manage_viewings: boolean;
  read_only_interest_fields_alert_visible: boolean;
  taken_off_marketplace: boolean;
  tenancy_details_visible: boolean;
}

export interface InterestScheduleData {
  all_columns: Record<string, any>;
  disposal_id: unknown;
  fields_available: FieldConfigs;
  fields_disabled: string[];
  fields_fixed_create: string[];
  fields_fixed_edit: string[];
  investment: unknown;
  locked: unknown;
  preferred_columns: string[];
  preferred_status_for_match_requests: number;
  preferred_status_for_portal_enquiries: number;
  preferred_status_for_outbound_society_match_requests: number;
}

export enum InterestScheduleCustomAction {
  ClearDiscountedReason = 'clearDiscountedReason',
  Discount = 'discount',
  HideFromSchedule = 'hideFromSchedule',
  ShowOnSchedule = 'ShowOnSchedule',
}

// ------ Schedule Match ---------------------------------------------------------------------------

export enum ScheduleMatchGroups {
  Active = 'active',
  Discounted = 'discounted',
  NotMatched = 'not_matched',
  Pending = 'pending',
}

export enum ScheduleMatchStatusActions {
  Complete = 'complete',
  CreateViewing = 'create_viewing',
}

export enum ScheduleMatchStatusTheme {
  Amber = 'amber',
  Blue = 'blue',
  Green = 'green',
  Grey = 'grey',
  Neutral = 'neutral',
  Primary = 'primary',
  Purple = 'purple',
  Red = 'red',
}

export type ScheduleMatchStatuses = InterestScheduleStatuses | AvailabilityScheduleStatuses;

export enum ScheduleMatchStatusGroups {
  BidPlaced = 'bid_placed',
  Completed = 'completed',
  Confidential = 'confidential',
  DetailsShared = 'details_shared',
  Discounted = 'discounted',
  Enquiries = 'enquiries',
  Idle = 'idle',
  Longlist = 'longlist',
  MatchRequests = 'match_requests',
  Negotiating = 'negotiating',
  NotSent = 'not_sent',
  Progressing = 'progressing',
  Prospect = 'prospect',
  ReceivedTeaser = 'received_teaser',
  RequestSent = 'request_sent',
  Shortlist = 'shortlist',
  Signed = 'signed',
  TeaserSent = 'teaser_sent',
  TermsAgreed = 'terms_agreed',
  UnderOffer = 'under_offer',
  Viewing = 'viewing',
}
// Custom groups
export const SCHEDULE_MATCH_ALL_GROUP = 'all' as const;

export type ScheduleMatchStatusGroupsWithAll = ScheduleMatchStatusGroups | typeof SCHEDULE_MATCH_ALL_GROUP;

export interface RequirementMatchedSpace {
  address: string;
  address_name: string;
  comment_for_applicant: string;
  created_at: string;
  discounted: number | null;
  discounted_reason: string;
  disposal_assigned_users_pa_string: string;
  disposal_confidential: boolean;
  disposal_context: boolean;
  disposal_id: number;
  id: number;
  image: string;
  latest_comment: IComment | null;
  postcode: string;
  requirement_context: boolean;
  requirement_id: number;
  shortlist_sort: number;
  shortlist_status: ScheduleMatchStatuses;
  shortlist_status_string: string;
  size: string;
  society_match: boolean;
  total_comments: number;
  updated_at: string;
  /** @deprecated Use the context-aware `discounted` instead. */
  interest_discounted: number | null;
  /** @deprecated Use the context-aware `discounted_reason` instead. */
  interest_discounted_string: string;
  /** @deprecated Use the context-aware `shortlist_sort` instead. */
  interest_sort: number;
  /** @deprecated Use the context-aware `shortlist_status` instead. */
  interest_status: InterestScheduleStatuses;
  /** @deprecated Use the context-aware `shortlist_status_string` instead. */
  interest_status_string: string;
  /** @deprecated Use the context-aware `discounted` instead. */
  opportunity_discounted: number | null;
  /** @deprecated Use the context-aware `discounted_reason` instead. */
  opportunity_discounted_string: string;
  /** @deprecated Use the context-aware `shortlist_sort` instead. */
  opportunity_sort: number;
  /** @deprecated Use the context-aware `shortlist_status` instead. */
  opportunity_status: AvailabilityScheduleStatuses;
  /** @deprecated Use the context-aware `shortlist_status_string` instead. */
  opportunity_status_string: string;
}

export interface ScheduleMatchStatusGroup {
  active: boolean;
  description: string;
  export: boolean;
  insights_description: string;
  manage: boolean;
  order_by?: string;
  statuses: ScheduleMatchStatuses[] | null;
  title: string;
}

export interface ScheduleMatchStatusConfig {
  action: ScheduleMatchStatusActions | null;
  label: string;
  match_group: ScheduleMatchGroups | null;
  show_if_status?: boolean;
  theme: ScheduleMatchStatusTheme;
}

// NOTE: Base config that is used by both FE and BE
export interface ScheduleMatchBaseConfig {
  actionStatusesMap: Record<ScheduleMatchStatusActions, ScheduleMatchStatuses[]>;
  groupConfig: Record<ScheduleMatchStatusGroups, ScheduleMatchStatusGroup>;
  statusActionsMap: Record<ScheduleMatchStatuses, ScheduleMatchStatusActions>;
  statusConfig: Record<ScheduleMatchStatuses, ScheduleMatchStatusConfig>;
  statuses: ScheduleMatchStatuses[];
  statusGroupsMap: Record<ScheduleMatchStatuses, ScheduleMatchStatusGroups>;
  statusLabelsMap: Record<ScheduleMatchStatuses, string>;
  statusThemesMap: Record<ScheduleMatchStatuses, ScheduleMatchStatusTheme>;
}

// NOTE: BE provided - availablility schedule config
export interface AvailabilityScheduleConfig extends ScheduleMatchBaseConfig {
  exportGroups: ScheduleMatchStatusGroups[];
  presentGroups: ScheduleMatchStatusGroups[];
  scheduleGroups: ScheduleMatchStatusGroups[];
  scheduleGroupsActive: ScheduleMatchStatusGroups[];
}

export const availabilityScheduleConfigGroupKeys = [
  'scheduleGroups',
  'scheduleGroupsActive',
] as const satisfies readonly (keyof AvailabilityScheduleConfig)[];

// NOTE: BE provided - interest schedule config
export interface InterestScheduleConfig extends ScheduleMatchBaseConfig {
  exportGroups: ScheduleMatchStatusGroups[];
  insightGroups: ScheduleMatchStatusGroups[];
  presentGroups: ScheduleMatchStatusGroups[];
  presentGroupsActive: ScheduleMatchStatusGroups[];
  scheduleGroups: ScheduleMatchStatusGroups[];
  scheduleGroupsActive: ScheduleMatchStatusGroups[];
}

// NOTE: Consumed by FE depending on context
export interface InterestScheduleContextConfig extends ScheduleMatchBaseConfig {
  exportGroups: ScheduleMatchStatusGroups[];
  statusDropdownOptions: InterestScheduleStatusDropdownOption[];
  statusGroups: ScheduleMatchStatusGroups[];
  statusGroupsActive: ScheduleMatchStatusGroups[];
  statusOptions: Option<InterestScheduleStatuses>[];
}

export const interestScheduleConfigGroupKeys = [
  'exportGroups',
  'presentGroups',
  'presentGroupsActive',
  'scheduleGroups',
  'scheduleGroupsActive',
] as const satisfies readonly (keyof InterestScheduleConfig)[];

// The opening match message shown as a highlight in a disposal summary (body + author),
// derived from the earliest message in the thread. Distinct from MatchMessageItem (a list row).
export interface MatchMessageOnDisplay {
  body: string;
  user: BasicUser;
}
